import React, { FC, ReactNode } from "react";
import { observer } from 'mobx-react-lite';
import icons from '../../../icons';
import cx from 'classnames';
import './Accordion.css';
import { useCollapsibleState } from '../../../components/collapsible_state';

interface AccordionProps {
    title: string | ReactNode;
    id?: string;
    isDefaultOpen?: boolean;
    keepOpenState?: boolean;
    altActionIcon?: string;
    altActionCallback?: () => void;
    children?: ReactNode;
    className?: string;
    altActionDisabled?: boolean;
    altActionId?: string;
}

/**
 * Accordion component
 * @param title - title of the accordion
 * @param isDefaultOpen - whether the accordion is open by default
 * @param altActionIcon - icon to be displayed on the right side of the accordion header
 * @param altActionCallback - callback to be called when the altActionIcon is clicked
 * @param children - content of the accordion
 * @returns Accordion component
 */
export const Accordion: FC<AccordionProps> = observer(({
  title,
  id,
  isDefaultOpen = false,
  keepOpenState = false,
  altActionIcon,
  altActionCallback,
  children,
  className,
  altActionDisabled = false,
  altActionId,
}) => {

  const [isOpen, setIsOpen] = useCollapsibleState({
    id: `${id || title}`,
    keepState: keepOpenState,
    defaultIsOpen: isDefaultOpen,
  });

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleAltActionCallback = (e) => {
    if (altActionCallback) {
      e.stopPropagation();
      altActionCallback();

      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  return (
    <div className={cx('accordion', className)}>
      <div onClick={handleToggle} className="accordion__header">
        <div className="accordion__header-title-container">
          { children !== undefined && <i className={cx(icons.caretDown, {"accordion__icon--closed": !isOpen})}></i>}
          <span className="accordion__header-title">
            {title}
          </span>
        </div>
        {altActionIcon && (
          <button
            id={altActionId}
            className="accordion__alt-action-button"
            onClick={handleAltActionCallback}
            disabled={altActionDisabled}
          >
            <i className={altActionIcon}></i>
          </button>
        )}
      </div>
      <div className={cx("accordion__content", {"accordion__content--closed": !isOpen || !children})}>
        {children}
      </div>
    </div>
  );
});